import { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../../config';
import { useTranslation } from 'react-i18next';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import PlaceIcon from '@mui/icons-material/Place';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FlagIcon from '@mui/icons-material/Flag';
import LocationCityIcon from '@mui/icons-material/LocationCity';

interface DashboardStats {
  totalPins: number;
  totalPhotos: number;
  totalCountries: number;
  totalCities: number;
}

interface TimelineEntry {
  city: string;
  countryCode: string;
  firstVisit: string;
  pinCount: number;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [visitedContinents, setVisitedContinents] = useState<string[]>([]);
  const [topCitiesVisited, setTopCitiesVisited] = useState(0);
  const [visitedCountryNames, setVisitedCountryNames] = useState<string[]>([]);
  const [visitedTopCityNames, setVisitedTopCityNames] = useState<string[]>([]);
  const [countryPinCounts, setCountryPinCounts] = useState<Record<string, number>>({});
  const [visitedWonders, setVisitedWonders] = useState<string[]>([]);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [trophies, setTrophies] = useState<string[]>([]);
  const heatmapRef = useRef<HTMLDivElement>(null);
  const heatmapMapRef = useRef<mapboxgl.Map | null>(null);
  const ALL_CONTINENTS = ['África', 'Antártida', 'Ásia', 'Europa', 'América do Norte', 'Oceania', 'América do Sul'];
  const ALL_WONDERS = ['Cristo Redentor', 'Machu Picchu', 'Chichen Itza', 'Coliseu', 'Taj Mahal', 'Muralha da China', 'Petra'];

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`${API_BASE_URL}/api/v1/analytics/me/dashboard`, {
          headers: token ? { Authorization: 'Bearer ' + token } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setStats({
            totalPins: data.stats?.total_pins || data.stats?.totalPins || data.totalPins || 0,
            totalPhotos: data.stats?.total_photos || data.stats?.totalPhotos || data.totalPhotos || 0,
            totalCountries: data.stats?.total_countries || data.stats?.totalCountries || data.totalCountries || 0,
            totalCities: data.stats?.total_cities || data.stats?.totalCities || data.totalCities || 0,
          });
          if (data.visitedContinents) setVisitedContinents(data.visitedContinents);
          if (data.topCitiesVisited !== undefined) setTopCitiesVisited(data.topCitiesVisited);
          if (data.visitedCountries) setVisitedCountryNames(data.visitedCountries);
          if (data.visitedTopCities) setVisitedTopCityNames(data.visitedTopCities);
          if (data.visitedWonders) setVisitedWonders(data.visitedWonders);
          if (data.timeline) setTimeline(data.timeline);
          // Fetch trophies
          try {
            const tres = await fetch(`${API_BASE_URL}/api/v1/trophies/me`, { headers: token ? { Authorization: 'Bearer ' + token } : {} });
            if (tres.ok) { const tdata = await tres.json(); setTrophies((tdata.trophies || []).map((t: any) => t.trophy_type)); }
          } catch {}
          // Fetch heatmap data
          try {
            const hres = await fetch(`${API_BASE_URL}/api/v1/analytics/me/heatmap`, {
              headers: token ? { Authorization: 'Bearer ' + token } : {},
            });
            if (hres.ok) {
              const hdata = await hres.json();
              const counts: Record<string, number> = {};
              (hdata.countries || hdata.entries || []).forEach((e: any) => {
                const code = e.country_code || e.countryCode;
                const cnt = e.pin_count || e.pinCount || 0;
                if (code) counts[code] = cnt;
              });
              setCountryPinCounts(counts);
            }
          } catch {}
        }
      } catch {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  // Render heatmap
  useEffect(() => {
    if (!heatmapRef.current || heatmapMapRef.current) return;
    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';
    const map = new mapboxgl.Map({
      container: heatmapRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [0, -10],
      zoom: 0.7,
      projection: 'equalEarth' as any,
      interactive: false,
      attributionControl: false,
      scrollZoom: false,
      boxZoom: false,
      dragRotate: false,
      dragPan: false,
      keyboard: false,
      doubleClickZoom: false,
      touchZoomRotate: false,
    });
    heatmapMapRef.current = map;
    map.on('load', () => {
      const countries = Object.keys(countryPinCounts);
      if (countries.length === 0) return; // Base map shows fine without any overlay
      const maxPins = Math.max(...Object.values(countryPinCounts), 1);
      map.addSource('countries', { type: 'vector', url: 'mapbox://mapbox.country-boundaries-v1' });
      const matchExpr: any[] = ['match', ['get', 'iso_3166_1']];
      countries.forEach(code => {
        const intensity = countryPinCounts[code] / maxPins;
        // Blue brand: light #90caf9 (144,202,249) to dark #0d47a1 (13,71,161)
        const r = Math.round(144 - intensity * 131);
        const g = Math.round(202 - intensity * 131);
        const b = Math.round(249 - intensity * 88);
        matchExpr.push(code.toUpperCase(), `rgba(${r},${g},${b},0.7)`);
      });
      matchExpr.push('rgba(0,0,0,0)');
      map.addLayer({
        id: 'country-fills',
        type: 'fill',
        source: 'countries',
        'source-layer': 'country_boundaries',
        paint: { 'fill-color': matchExpr as any },
      });
    });
    return () => { map.remove(); heatmapMapRef.current = null; };
  }, [countryPinCounts, loading]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const statCards = [
    { label: t('dashboard.totalPins'), value: stats?.totalPins || 0, icon: PlaceIcon, color: '#d32f2f' },
    { label: t('dashboard.totalPhotos'), value: stats?.totalPhotos || 0, icon: PhotoCameraIcon, color: '#1565c0' },
    { label: t('dashboard.countriesVisited'), value: stats?.totalCountries || 0, icon: FlagIcon, color: '#1565c0' },
    { label: t('dashboard.cities'), value: stats?.totalCities || 0, icon: LocationCityIcon, color: '#42a5f5' },
  ];

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {statCards.map((card) => (
          <Grid item xs={6} md={3} key={card.label}>
            <Card sx={{ height: '100%', borderTop: `4px solid ${card.color}` }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <card.icon sx={{ fontSize: 40, color: card.color, mb: 1 }} />
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Timeline Chart - Zig Zag horizontal */}
      {timeline.length > 0 && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>{t('dashboard.timeline')}</Typography>
          <Box sx={{ position: 'relative', overflowX: 'auto', overflowY: 'hidden', pb: 2 }}>
            {/* Horizontal center line */}
            <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 2, bgcolor: 'divider', transform: 'translateY(-1px)' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: timeline.slice(-15).length * 95, position: 'relative', py: 8 }}>
              {timeline.slice(-15).map((entry, idx) => {
                const isTop = idx % 2 === 0;
                return (
                  <Box key={`${entry.city}-${idx}`} sx={{ flex: '0 0 95px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                    {/* Content above or below */}
                    <Box sx={{
                      position: 'absolute',
                      [isTop ? 'bottom' : 'top']: '50%',
                      [isTop ? 'mb' : 'mt']: 0,
                      transform: isTop ? 'translateY(-20px)' : 'translateY(20px)',
                      textAlign: 'center',
                      width: 90,
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {entry.city}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                        {formatDate(entry.firstVisit)}
                      </Typography>
                      {entry.countryCode && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {entry.countryCode.toUpperCase()}
                        </Typography>
                      )}
                    </Box>
                    {/* Dot on the line */}
                    <Box sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, borderRadius: '50%', bgcolor: 'primary.main', border: '3px solid #fff', boxShadow: 2, zIndex: 2 }} />
                    {/* Connector line from dot to content */}
                    <Box sx={{ position: 'absolute', top: '50%', width: 2, height: 20, bgcolor: 'primary.main', transform: isTop ? 'translateY(-100%)' : 'translateY(0)', zIndex: 1 }} />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      {/* Exploration progress */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>{t('dashboard.exploration')}</Typography>
        <Grid container spacing={3}>
          {/* Países */}
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('dashboard.countriesWorld')}</Typography>
                  {trophies.includes('all_countries') && <Typography sx={{ fontSize: '1.2rem' }}>{String.fromCodePoint(0x1F30D)}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats?.totalCountries || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">/ 195</Typography>
                </Box>
                <LinearProgress variant="determinate" value={((stats?.totalCountries || 0) / 195) * 100} sx={{ height: 8, borderRadius: 4 }} color="success" />
                <Typography variant="caption" color="text.secondary">{(((stats?.totalCountries || 0) / 195) * 100).toFixed(1)}% {t("dashboard.ofCountries")}</Typography>
                {visitedCountryNames.length > 0 && (
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                    {visitedCountryNames.join(', ')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Continentes */}
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('dashboard.continents')}</Typography>
                  {trophies.includes('all_continents') && <Typography sx={{ fontSize: '1.2rem' }}>{String.fromCodePoint(0x1F9ED)}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>{visitedContinents.length}</Typography>
                  <Typography variant="body2" color="text.secondary">/ 7</Typography>
                </Box>
                <LinearProgress variant="determinate" value={(visitedContinents.length / 7) * 100} sx={{ height: 8, borderRadius: 4 }} />
                <Typography variant="caption" color="text.secondary">{((visitedContinents.length / 7) * 100).toFixed(1)}% {t("dashboard.ofContinents")}</Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {ALL_CONTINENTS.map((c, i) => (
                    <Typography key={c} variant="caption" component="span" sx={{ color: 'text.secondary', fontWeight: visitedContinents.includes(c) ? 700 : 400 }}>
                      {c}{i < ALL_CONTINENTS.length - 1 ? ', ' : ''}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 100 Maiores Cidades */}
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('dashboard.topCities')}</Typography>
                  {trophies.includes('top_cities') && <Typography sx={{ fontSize: '1.2rem' }}>{String.fromCodePoint(0x1F3D9)}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>{topCitiesVisited}</Typography>
                  <Typography variant="body2" color="text.secondary">/ 100</Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min((topCitiesVisited / 100) * 100, 100)} sx={{ height: 8, borderRadius: 4 }} color="secondary" />
                <Typography variant="caption" color="text.secondary">{((topCitiesVisited / 100) * 100).toFixed(1)}% {t("dashboard.ofTopCities")}</Typography>
                {visitedTopCityNames.length > 0 && (
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                    {visitedTopCityNames.join(', ')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Maravilhas do Mundo */}
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('dashboard.wonders')}</Typography>
                  {trophies.includes('all_wonders') && <Typography sx={{ fontSize: '1.2rem' }}>{String.fromCodePoint(0x2B50)}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>{visitedWonders.length}</Typography>
                  <Typography variant="body2" color="text.secondary">/ 7</Typography>
                </Box>
                <LinearProgress variant="determinate" value={(visitedWonders.length / 7) * 100} sx={{ height: 8, borderRadius: 4 }} color="warning" />
                <Typography variant="caption" color="text.secondary">{((visitedWonders.length / 7) * 100).toFixed(1)}% {t("dashboard.ofWonders")}</Typography>
                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {ALL_WONDERS.map((w, i) => (
                    <Typography key={w} variant="caption" component="span" sx={{ color: 'text.secondary', fontWeight: visitedWonders.includes(w) ? 700 : 400 }}>
                      {w}{i < ALL_WONDERS.length - 1 ? ', ' : ''}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      {/* World Heat Map */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('dashboard.heatmap')}</Typography>
        <Box ref={heatmapRef} sx={{ width: '100%', height: 550, borderRadius: 2, overflow: 'hidden' }} />
        {/* Legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, justifyContent: 'flex-end' }}>
          <Typography variant="caption" color="text.secondary">{t('dashboard.lessPlins')}</Typography>
          <Box sx={{ display: 'flex', height: 12, borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ width: 30, bgcolor: 'rgba(144,202,249,0.7)' }} />
            <Box sx={{ width: 30, bgcolor: 'rgba(66,165,245,0.7)' }} />
            <Box sx={{ width: 30, bgcolor: 'rgba(21,101,192,0.7)' }} />
            <Box sx={{ width: 30, bgcolor: 'rgba(13,71,161,0.7)' }} />
            <Box sx={{ width: 30, bgcolor: 'rgba(13,71,161,0.9)' }} />
          </Box>
          <Typography variant="caption" color="text.secondary">{t('dashboard.morePins')}</Typography>
        </Box>
      </Box>


      </Box>
    </Container>
  );
}